import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/auth';
import { useConfig } from '@/config';
import { useTelemetry } from '@/telemetry';
import { createAitaClient, type AitaClient } from '@/api/aitaClient';
import { clearRetakeMarker } from './retakeMarker';
import type {
  AssessmentContent,
  InProgressAssessmentDto,
  PartnerDetailsDto,
  ResponsesMap,
  SubmittedAssessmentDto,
} from '@/api/types';

export type InitStatus = 'loading' | 'ready' | 'error';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type SubmitOutcome =
  | { kind: 'ok'; dto: SubmittedAssessmentDto }
  | { kind: 'stale' }
  /** code/fields surface the API submit-validation cause (e.g. other_text_required) */
  | { kind: 'validation'; code?: string; fields?: string[] }
  | { kind: 'error' };

export interface AssessmentContextValue {
  initStatus: InitStatus;
  /** HTTP status of the failed init call (e.g. 403 = tenant not eligible), if known */
  initErrorStatus: number | null;
  content: AssessmentContent | null;
  /** Whether a server-side in-progress document exists */
  hasInProgress: boolean;
  /** Most recent submitted assessment — only resolved when there is NO in-progress doc */
  latest: SubmittedAssessmentDto | null;
  partnerDetails: PartnerDetailsDto | null;
  responses: ResponsesMap;
  saveStatus: SaveStatus;
  /** True once after a concurrent-write adoption (412/409 → server copy wins) */
  staleNotice: boolean;
  dismissStaleNotice(): void;
  /** Record an answer (1..3) or an explicit skip (null). Auto-saves (debounced). */
  answer(questionId: string, value: number | null): void;
  /** Validate-and-save partner details (immediate flush). Resolves true on success. */
  savePartnerDetails(details: PartnerDetailsDto): Promise<boolean>;
  /** Cancel the debounce and drain all pending saves. Resolves true when clean. */
  flush(): Promise<boolean>;
  /** Flush, then POST submit. The API reads the Cosmos doc, not a request body. */
  submit(): Promise<SubmitOutcome>;
  /** Re-attempt after a save error */
  retrySave(): void;
  /** Retake: clear local assessment state (server doc was deleted by submit). */
  reset(): void;
  retryInit(): void;
  /** Escape hatch for Results-page fetch-by-id and the PDF download path */
  client: AitaClient;
}

const AssessmentContext = createContext<AssessmentContextValue | null>(null);

const SAVE_DEBOUNCE_MS = 500;
const IDEMPOTENCY_STORAGE_KEY = 'aita-submit-idempotency-key';
/** Pre-auth-redirect stash: unsaved answers survive an acquireTokenRedirect round-trip. */
const PENDING_STASH_KEY = 'aita-pending-state';
const PENDING_STASH_MAX_AGE_MS = 30 * 60 * 1000;

interface PendingStash {
  /** oid of the account the stash was written under — never replay across accounts */
  oid: string | null;
  details: PartnerDetailsDto | null;
  responses: ResponsesMap;
  savedAt: number;
}

/** Best-effort idempotency-key cleanup — a storage exception must never break flow. */
function clearIdempotencyKey(): void {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
  } catch {
    // Storage unavailable — the key (if any) expires with the session.
  }
}

/** Reads and clears the pre-redirect stash; null when absent, stale, or unparsable. */
function consumePendingStash(): PendingStash | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_STASH_KEY);
    sessionStorage.removeItem(PENDING_STASH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingStash;
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > PENDING_STASH_MAX_AGE_MS) {
      return null;
    }
    // Sanitize value-by-value rather than trusting the blob: saves are
    // full-replacement, so one corrupt entry merged here would 400 EVERY
    // subsequent save for the rest of the session.
    const responses: ResponsesMap = {};
    if (parsed.responses && typeof parsed.responses === 'object') {
      for (const [questionId, value] of Object.entries(parsed.responses)) {
        if (value === null || value === 1 || value === 2 || value === 3) {
          responses[questionId] = value;
        }
      }
    }
    return {
      oid: typeof parsed.oid === 'string' ? parsed.oid : null,
      details: parsed.details && typeof parsed.details === 'object' ? parsed.details : null,
      responses,
      savedAt: parsed.savedAt,
    };
  } catch {
    return null;
  }
}

export function AssessmentProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { acquireTokenSilent, user } = useAuth();
  const config = useConfig();
  const { trackEvent } = useTelemetry();
  const userOid = user?.oid ?? null;

  const client = useMemo(
    () =>
      createAitaClient({
        acquireToken: async () => {
          // ID-token migration (WI 776915): send the ID token when present, falling back to the
          // access token for legacy paths / test stubs that only populate accessToken.
          const token = await acquireTokenSilent();
          return token.idToken ?? token.accessToken;
        },
        baseUrl: config.apiBaseUrl,
      }),
    [acquireTokenSilent, config.apiBaseUrl],
  );

  const [initStatus, setInitStatus] = useState<InitStatus>('loading');
  const [initErrorStatus, setInitErrorStatus] = useState<number | null>(null);
  const [content, setContent] = useState<AssessmentContent | null>(null);
  const [hasInProgress, setHasInProgress] = useState(false);
  const [latest, setLatest] = useState<SubmittedAssessmentDto | null>(null);
  const [partnerDetails, setPartnerDetails] = useState<PartnerDetailsDto | null>(null);
  const [responses, setResponses] = useState<ResponsesMap>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [staleNotice, setStaleNotice] = useState(false);
  const [initNonce, setInitNonce] = useState(0);

  // The save engine reads through refs so debounced/queued saves never see stale state.
  const detailsRef = useRef<PartnerDetailsDto | null>(null);
  const responsesRef = useRef<ResponsesMap>({});
  const etagRef = useRef<string | null>(null);
  const hasInProgressRef = useRef(false);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Mirror of `content` for async callbacks (their useCallback deps omit state). */
  const contentRef = useRef<AssessmentContent | null>(null);
  /** Bumped on every server-copy adoption — lets submit() detect a flush-time adoption. */
  const adoptionGenRef = useRef(0);
  /**
   * startedAt of the live server draft. Submit copies it verbatim onto the
   * submitted doc, so it identifies "the submission born from THIS draft" in
   * the no-in-progress recovery below — exact and clock-free, unlike a
   * recency window on submittedAt.
   */
  const draftStartedAtRef = useRef<string | null>(null);

  const adoptServerCopy = useCallback((dto: InProgressAssessmentDto, etag: string | null) => {
    adoptionGenRef.current += 1;
    detailsRef.current = dto.partnerDetails;
    responsesRef.current = dto.responses;
    etagRef.current = etag;
    draftStartedAtRef.current = dto.startedAt;
    hasInProgressRef.current = true;
    setPartnerDetails(dto.partnerDetails);
    setResponses(dto.responses);
    setHasInProgress(true);
  }, []);

  /**
   * After a successful submit the server draft is gone — drop the local copy
   * too. Keeping it would let a Back-navigated Review/Details screen render a
   * draft that can never be submitted again (its etag died with the doc), and
   * whose next save would silently clone the submitted answers into a fresh
   * server draft.
   */
  const adoptSubmitted = useCallback((dto: SubmittedAssessmentDto) => {
    clearIdempotencyKey();
    etagRef.current = null;
    draftStartedAtRef.current = null;
    hasInProgressRef.current = false;
    dirtyRef.current = false;
    detailsRef.current = null;
    responsesRef.current = {};
    setHasInProgress(false);
    setPartnerDetails(null);
    setResponses({});
    setSaveStatus('idle');
    setLatest(dto);
  }, []);

  // -------------------------------------------------------------------------
  // Init: resume in-progress (content pinned to its version) or start fresh
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    function failInit(httpStatus?: number): void {
      setInitErrorStatus(httpStatus ?? null);
      setInitStatus('error');
      trackEvent('aita.init.error', httpStatus !== undefined ? { httpStatus } : undefined);
    }

    /** Re-applies answers stashed before an auth redirect (no data loss, WI 763388). */
    function restorePendingStash(): void {
      const stash = consumePendingStash();
      if (!stash) return;
      if (stash.oid !== userOid) {
        // Stashed under a different signed-in account (the user switched at
        // the auth prompt) — never replay one account's answers into
        // another's assessment. consumePendingStash already cleared it.
        return;
      }
      // Keys must belong to the loaded content — an unknown question id in the
      // full-replacement body fails the whole save, poisoning every retry.
      const validIds = new Set(
        (contentRef.current?.dimensions ?? []).flatMap((d) => d.questions.map((q) => q.id)),
      );
      const restored: ResponsesMap = {};
      for (const [questionId, value] of Object.entries(stash.responses)) {
        if (validIds.has(questionId)) restored[questionId] = value;
      }
      if (Object.keys(restored).length === 0 && !stash.details) return;
      const mergedResponses = { ...responsesRef.current, ...restored };
      responsesRef.current = mergedResponses;
      setResponses(mergedResponses);
      if (stash.details) {
        detailsRef.current = stash.details;
        setPartnerDetails(stash.details);
      }
      dirtyRef.current = true;
      trackEvent('aita.save.stash_restored');
      void performSave();
    }

    async function init(): Promise<void> {
      setInitStatus('loading');
      setInitErrorStatus(null);
      const inProgress = await client.getInProgress();
      if (cancelled) return;

      if (inProgress.status === 'ok') {
        const contentResult = await client.getContent(inProgress.dto.contentVersion);
        if (cancelled) return;
        if (contentResult.status !== 'ok') {
          failInit(contentResult.status === 'error' ? contentResult.httpStatus : undefined);
          return;
        }
        contentRef.current = contentResult.content;
        setContent(contentResult.content);
        adoptServerCopy(inProgress.dto, inProgress.etag);
        restorePendingStash();
        setInitStatus('ready');
        return;
      }

      if (inProgress.status === 'not-found') {
        const [contentResult, latestResult] = await Promise.all([
          client.getActiveContent(),
          client.getLatest(),
        ]);
        if (cancelled) return;
        if (contentResult.status !== 'ok') {
          failInit(contentResult.status === 'error' ? contentResult.httpStatus : undefined);
          return;
        }
        if (latestResult.status === 'error') {
          // A failed latest-lookup must not impersonate a fresh start — the
          // returning user's results would silently appear lost (and Entry
          // would route them into a brand-new assessment). Same recoverable
          // error card as a content failure; only 'not-found' means fresh.
          failInit(latestResult.httpStatus);
          return;
        }
        contentRef.current = contentResult.content;
        setContent(contentResult.content);
        setLatest(latestResult.status === 'ok' ? latestResult.dto : null);
        hasInProgressRef.current = false;
        setHasInProgress(false);
        restorePendingStash();
        setInitStatus('ready');
        return;
      }

      failInit(inProgress.httpStatus);
    }

    void init();
    return () => {
      cancelled = true;
    };
    // performSave/trackEvent are stable refs from this provider; init must only
    // re-run when the client, the signed-in account, or an explicit retry changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, adoptServerCopy, initNonce, userOid]);

  // A live in-progress attempt supersedes any retake-pending marker — the marker
  // only bridges the gap between confirming a retake on Results and the new
  // draft's first save (entry routing reads it; see EntryPage).
  useEffect(() => {
    if (hasInProgress) clearRetakeMarker(userOid);
  }, [hasInProgress, userOid]);

  // Stash unsaved work just before an interactive auth redirect navigates away.
  useEffect(() => {
    function stash(): void {
      if (!dirtyRef.current && inFlightRef.current === null) return;
      try {
        const payload: PendingStash = {
          oid: userOid,
          details: detailsRef.current,
          responses: responsesRef.current,
          savedAt: Date.now(),
        };
        sessionStorage.setItem(PENDING_STASH_KEY, JSON.stringify(payload));
        trackEvent('aita.save.stashed_for_redirect');
      } catch {
        // Storage unavailable — at most one debounced answer can be lost.
      }
    }
    window.addEventListener('aita:before-auth-redirect', stash);
    return () => window.removeEventListener('aita:before-auth-redirect', stash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOid]);

  // -------------------------------------------------------------------------
  // Save engine: debounced, full-replacement, in-flight guarded (latest wins)
  // -------------------------------------------------------------------------
  const performSave = useCallback((): Promise<boolean> => {
    if (inFlightRef.current) return inFlightRef.current;

    const attempt = (async (): Promise<boolean> => {
      while (dirtyRef.current) {
        dirtyRef.current = false;
        const body = {
          partnerDetails: detailsRef.current ?? undefined,
          responses: responsesRef.current,
        };
        const result = await client.saveInProgress(
          body,
          hasInProgressRef.current ? etagRef.current : undefined,
        );

        if (result.status === 'ok') {
          etagRef.current = result.etag;
          draftStartedAtRef.current = result.dto.startedAt;
          hasInProgressRef.current = true;
          setHasInProgress(true);
          if (result.dto.contentVersion !== contentRef.current?.version) {
            // The server pinned the draft to a different content version than
            // the questions we're showing (active content rolled over between
            // our content load and this create-save). The save itself
            // succeeded, so finish the loop normally — but re-init so the
            // provider re-pins content to the doc's version.
            setInitNonce((n) => n + 1);
          }
          continue; // drain any mutation that landed mid-flight
        }

        if (result.status === 'stale' || result.status === 'collision') {
          // Concurrent writer (another tab) — recover from what the server has.
          // Snapshot the refs first: anything the user edits WHILE the recovery
          // fetch is in flight is newer than both conflicting copies and must
          // survive the adoption below (answer() replaces the responses object,
          // so during-recovery edits are detectable by value comparison).
          const responsesAtRecoveryStart = responsesRef.current;
          const detailsAtRecoveryStart = detailsRef.current;
          const server = await client.getInProgress();
          if (server.status === 'ok') {
            trackEvent('aita.save.stale_adopted', { reason: result.status });
            if (server.dto.contentVersion !== contentRef.current?.version) {
              // The server draft is on a different content version (another
              // window submitted and started fresh after a rollover): adopting
              // its responses against the loaded questions would bind answers
              // to the wrong prompts. Re-init instead — it re-adopts the doc
              // AND re-pins content to the doc's version.
              setStaleNotice(true);
              setSaveStatus('idle');
              setInitNonce((n) => n + 1);
              return false;
            }
            const duringRecovery: ResponsesMap = {};
            for (const [questionId, value] of Object.entries(responsesRef.current)) {
              if (responsesAtRecoveryStart[questionId] !== value) {
                duringRecovery[questionId] = value;
              }
            }
            const detailsDuringRecovery =
              detailsRef.current !== detailsAtRecoveryStart ? detailsRef.current : null;
            adoptServerCopy(server.dto, server.etag);
            // Replay during-recovery edits over the adopted copy and keep the
            // engine dirty so the drain iteration persists them.
            if (Object.keys(duringRecovery).length > 0) {
              const merged = { ...responsesRef.current, ...duringRecovery };
              responsesRef.current = merged;
              setResponses(merged);
              dirtyRef.current = true;
            }
            if (detailsDuringRecovery !== null) {
              detailsRef.current = detailsDuringRecovery;
              setPartnerDetails(detailsDuringRecovery);
              dirtyRef.current = true;
            }
            setStaleNotice(true);
            continue;
          }
          if (server.status === 'not-found') {
            // The draft no longer exists — another window submitted it (submit
            // deletes the doc). Keep this window's work: drop the dead etag and
            // let the loop re-create the doc as a fresh draft (the next
            // iteration saves without If-Match).
            trackEvent('aita.save.recreated', { reason: result.status });
            etagRef.current = null;
            hasInProgressRef.current = false;
            setHasInProgress(false);
            dirtyRef.current = true;
            setStaleNotice(true);
            continue;
          }
          // Recovery fetch failed: restore the dirty flag (cleared at loop
          // entry) or retrySave() — which only acts when dirty — becomes a
          // permanent no-op and the error state can never be retried.
          dirtyRef.current = true;
          setSaveStatus('error');
          return false;
        }

        // Network/server error — keep state dirty for retry.
        dirtyRef.current = true;
        trackEvent(
          'aita.save.error',
          result.httpStatus !== undefined ? { httpStatus: result.httpStatus } : undefined,
        );
        setSaveStatus('error');
        return false;
      }
      setSaveStatus('saved');
      return true;
    })();

    inFlightRef.current = attempt;
    void attempt.finally(() => {
      inFlightRef.current = null;
    });
    return attempt;
  }, [client, adoptServerCopy, trackEvent]);

  const cancelDebounce = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true;
    setSaveStatus('saving');
    cancelDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void performSave();
    }, SAVE_DEBOUNCE_MS);
  }, [cancelDebounce, performSave]);

  // Live pointer for the unmount flush below: the flush effect's deps must not
  // include performSave (a dep change would run the cleanup mid-session).
  const performSaveRef = useRef(performSave);
  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  // Unmounting the /app subtree (browser Back to Landing) inside the debounce
  // window must not drop the pending answer: cancel the timer and fire one
  // last fire-and-forget save. Its setState calls no-op after unmount; the
  // refs it reads stay alive in the closure. An already-in-flight save drains
  // dirty state itself.
  useEffect(
    () => () => {
      cancelDebounce();
      if (dirtyRef.current && inFlightRef.current === null) {
        void performSaveRef.current();
      }
    },
    [cancelDebounce],
  );

  const flush = useCallback(async (): Promise<boolean> => {
    cancelDebounce();
    while (dirtyRef.current || inFlightRef.current) {
      const ok = await performSave();
      if (!ok) return false;
    }
    return true;
  }, [cancelDebounce, performSave]);

  // -------------------------------------------------------------------------
  // Public mutations
  // -------------------------------------------------------------------------
  const answer = useCallback(
    (questionId: string, value: number | null) => {
      const next = { ...responsesRef.current, [questionId]: value };
      responsesRef.current = next;
      setResponses(next);
      scheduleSave();
    },
    [scheduleSave],
  );

  const savePartnerDetails = useCallback(
    async (details: PartnerDetailsDto): Promise<boolean> => {
      detailsRef.current = details;
      setPartnerDetails(details);
      dirtyRef.current = true;
      setSaveStatus('saving');
      const adoptionGenBefore = adoptionGenRef.current;
      const ok = await flush();
      if (ok && adoptionGenRef.current !== adoptionGenBefore) {
        // The flush hit a concurrent write and adopted the server copy, so the
        // entered details were NOT persisted. Autosaved answers accept
        // server-wins silently, but an explicit form save must never report
        // success after discarding input: fail so the form stays put (the
        // stale notice explains why; a second Continue persists the user's
        // values over the now-fresh etag).
        return false;
      }
      return ok;
    },
    [flush],
  );

  const submit = useCallback(async (): Promise<SubmitOutcome> => {
    // The API submit reads the Cosmos doc — any unflushed answer would be lost.
    const adoptionGenBefore = adoptionGenRef.current;
    const flushed = await flush();
    if (!flushed || !etagRef.current) return { kind: 'error' };
    if (adoptionGenRef.current !== adoptionGenBefore) {
      // The flush hit a concurrent write and adopted the server copy (which
      // also refreshed our etag). Posting now would submit answers the user
      // never reviewed — surface the same refresh-and-stay outcome as a
      // submit-time 412 (the save path already raised staleNotice).
      return { kind: 'stale' };
    }

    // Storage access is best-effort (private-mode/quota policies can throw): a
    // key that fails to persist degrades retry dedup to this page lifetime, but
    // a storage exception must never reject submit() — the Review dialog awaits
    // an outcome, and a rejection would strand it in the submitting state.
    let key: string | null = null;
    try {
      key = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    } catch {
      key = null;
    }
    if (!key) {
      key = crypto.randomUUID();
      try {
        sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, key);
      } catch {
        // Non-persisted key — still valid for this attempt.
      }
    }

    const result = await client.submit(key, etagRef.current);

    switch (result.status) {
      case 'ok':
        adoptSubmitted(result.dto);
        return { kind: 'ok', dto: result.dto };

      case 'stale': {
        // Refresh-and-stay (plan §7D): adopt the server copy so Review re-renders current.
        const server = await client.getInProgress();
        if (server.status !== 'ok') {
          // Nothing was adopted — reporting 'stale' would tell the user to
          // review "the latest answers" that never refreshed, an unwinnable
          // loop (resubmit carries the same etag → 412 again). Plain error:
          // the dialog retry re-enters this path once the refresh works.
          return { kind: 'error' };
        }
        if (server.dto.contentVersion !== contentRef.current?.version) {
          // Server draft moved to a different content version — re-init to
          // re-pin content alongside the doc (see the save-path twin above).
          setStaleNotice(true);
          setInitNonce((n) => n + 1);
          return { kind: 'stale' };
        }
        adoptServerCopy(server.dto, server.etag);
        setStaleNotice(true);
        return { kind: 'stale' };
      }

      case 'validation':
        return {
          kind: 'validation',
          code: result.code,
          fields: result.fields,
        };

      case 'no-in-progress': {
        // The doc may already have been submitted (a concurrent tab completed
        // it, or a retry whose idempotency key regenerated after a storage
        // throw) — recover via the latest submission rather than presenting an
        // unwinnable retry loop. Adopt ONLY a submission born from this draft
        // (submit copies the draft's startedAt verbatim): anything else means
        // the draft vanished WITHOUT our answers being submitted (e.g. TTL
        // expiry), and adopting would silently wipe them behind an unrelated
        // results page.
        const latest = await client.getLatest();
        if (latest.status === 'ok' && latest.dto.startedAt === draftStartedAtRef.current) {
          trackEvent('aita.submit.latest_adopted');
          adoptSubmitted(latest.dto);
          return { kind: 'ok', dto: latest.dto };
        }
        if (latest.status !== 'error') {
          // Foreign-lineage latest, or no submission at all — the draft is
          // gone server-side. Drop the dead etag and mark dirty so the
          // dialog's retry re-creates the draft from local state (which still
          // holds every answer) instead of 404-looping. A transient latest
          // failure skips this: state is intact and retry re-enters cleanly.
          trackEvent('aita.submit.latest_mismatch');
          etagRef.current = null;
          draftStartedAtRef.current = null;
          hasInProgressRef.current = false;
          setHasInProgress(false);
          dirtyRef.current = true;
        }
        return { kind: 'error' };
      }

      default:
        return { kind: 'error' };
    }
  }, [client, flush, adoptServerCopy, adoptSubmitted, trackEvent]);

  const reset = useCallback(() => {
    cancelDebounce();
    dirtyRef.current = false;
    detailsRef.current = null;
    responsesRef.current = {};
    etagRef.current = null;
    draftStartedAtRef.current = null;
    hasInProgressRef.current = false;
    clearIdempotencyKey();
    setPartnerDetails(null);
    setResponses({});
    setHasInProgress(false);
    setSaveStatus('idle');
    setStaleNotice(false);
    // Retake starts a NEW assessment, and the next save creates the server doc
    // on the ACTIVE content version — but this provider may still hold content
    // pinned to the just-submitted draft's (possibly retired) version. Re-init
    // so the questions shown are the ones the new doc will be scored against.
    setInitNonce((n) => n + 1);
  }, [cancelDebounce]);

  const retrySave = useCallback(() => {
    if (dirtyRef.current) {
      trackEvent('aita.save.retry');
      setSaveStatus('saving');
      void performSave();
    }
  }, [performSave, trackEvent]);

  const retryInit = useCallback(() => setInitNonce((n) => n + 1), []);
  const dismissStaleNotice = useCallback(() => setStaleNotice(false), []);

  const value = useMemo<AssessmentContextValue>(
    () => ({
      initStatus,
      initErrorStatus,
      content,
      hasInProgress,
      latest,
      partnerDetails,
      responses,
      saveStatus,
      staleNotice,
      dismissStaleNotice,
      answer,
      savePartnerDetails,
      flush,
      submit,
      retrySave,
      reset,
      retryInit,
      client,
    }),
    [
      initStatus,
      initErrorStatus,
      content,
      hasInProgress,
      latest,
      partnerDetails,
      responses,
      saveStatus,
      staleNotice,
      dismissStaleNotice,
      answer,
      savePartnerDetails,
      flush,
      submit,
      retrySave,
      reset,
      retryInit,
      client,
    ],
  );

  return <AssessmentContext.Provider value={value}>{children}</AssessmentContext.Provider>;
}

export function useAssessment(): AssessmentContextValue {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
}

export { AssessmentContext };
